/**
 * Curated "must-see" stops for long road trips:
 *   - National parks & monuments
 *   - Iconic historic landmarks
 *   - Famous roadside quirks (World's Largest X, etc.)
 *
 * Surfaced as a dedicated "Worth a detour" category in DriveByOptions
 * ONLY when the trip is long (driving + ~80km+). For short urban hops
 * these would be irrelevant noise.
 *
 * Coords should sit on a routable road near the entrance/main parking
 * area so Mapbox snaps cleanly when added as a waypoint.
 */
import type { LngLat } from "@/utils/polyline";

export type EpicStopCategory = "national-park" | "landmark" | "quirky";

export interface EpicStop {
  coord: LngLat;
  name: string;
  category: EpicStopCategory;
  /** Tagline shown on hover / detail. */
  blurb?: string;
}

export const EPIC_ROAD_TRIP_STOPS: EpicStop[] = [
  // ===== National Parks & Monuments — US =====
  { coord: [-119.5383, 37.8651], name: "Yosemite National Park", category: "national-park", blurb: "Granite cliffs, waterfalls, giant sequoias" },
  { coord: [-119.4179, 36.4864], name: "Sequoia National Park", category: "national-park", blurb: "Largest trees on Earth" },
  { coord: [-122.1817, 41.7866], name: "Crater Lake National Park", category: "national-park", blurb: "Deepest lake in the US" },
  { coord: [-121.7681, 46.8523], name: "Mount Rainier National Park", category: "national-park", blurb: "Active volcano, alpine meadows" },
  { coord: [-123.4307, 47.8021], name: "Olympic National Park", category: "national-park", blurb: "Rainforest meets coastline" },
  { coord: [-110.5885, 44.4280], name: "Yellowstone National Park", category: "national-park", blurb: "Geysers, bison, supervolcano" },
  { coord: [-110.6818, 43.7904], name: "Grand Teton National Park", category: "national-park", blurb: "Iconic jagged peaks" },
  { coord: [-113.7870, 48.6962], name: "Glacier National Park", category: "national-park", blurb: "Going-to-the-Sun Road" },
  { coord: [-112.1130, 36.0544], name: "Grand Canyon (South Rim)", category: "national-park", blurb: "One mile deep" },
  { coord: [-111.5083, 37.2982], name: "Zion National Park", category: "national-park", blurb: "Red rock slot canyons" },
  { coord: [-112.1871, 37.5930], name: "Bryce Canyon National Park", category: "national-park", blurb: "Hoodoos at sunrise" },
  { coord: [-109.5925, 38.5733], name: "Arches National Park", category: "national-park", blurb: "2000+ stone arches" },
  { coord: [-109.8782, 38.3279], name: "Canyonlands National Park", category: "national-park", blurb: "Mesas carved by the Colorado" },
  { coord: [-105.6836, 40.3428], name: "Rocky Mountain National Park", category: "national-park", blurb: "Trail Ridge Road, alpine tundra" },
  { coord: [-103.4591, 43.8791], name: "Badlands National Park", category: "national-park", blurb: "Striped rock formations" },
  { coord: [-83.5070, 35.6118], name: "Great Smoky Mountains NP", category: "national-park", blurb: "Most-visited US park" },
  { coord: [-78.4767, 38.5290], name: "Shenandoah National Park", category: "national-park", blurb: "Skyline Drive vistas" },
  { coord: [-80.7820, 25.2866], name: "Everglades National Park", category: "national-park", blurb: "Subtropical wetlands" },
  { coord: [-116.0900, 36.4988], name: "Death Valley National Park", category: "national-park", blurb: "Lowest, hottest, driest" },
  { coord: [-116.0860, 33.8734], name: "Joshua Tree National Park", category: "national-park", blurb: "Mojave + Colorado deserts meet" },
  { coord: [-124.0046, 41.2132], name: "Redwood National Park", category: "national-park", blurb: "Tallest trees on Earth" },
  { coord: [-103.4592, 44.3387], name: "Mount Rushmore", category: "landmark", blurb: "Four presidents, 60 ft tall" },
  { coord: [-103.6296, 44.5868], name: "Devils Tower", category: "national-park", blurb: "America's first national monument" },
  { coord: [-110.4720, 36.9980], name: "Monument Valley", category: "landmark", blurb: "Iconic red mesas" },
  { coord: [-111.3743, 36.0594], name: "Antelope Canyon", category: "landmark", blurb: "Slot canyon light beams" },
  { coord: [-111.5104, 36.8744], name: "Horseshoe Bend", category: "landmark", blurb: "1000 ft above the Colorado River" },

  // ===== US Historic Landmarks =====
  { coord: [-122.4783, 37.8199], name: "Golden Gate Bridge", category: "landmark", blurb: "Iconic SF span" },
  { coord: [-122.4230, 37.8270], name: "Alcatraz Island", category: "landmark", blurb: "The Rock" },
  { coord: [-118.1310, 34.1341], name: "Hollywood Sign", category: "landmark", blurb: "World-famous letters" },
  { coord: [-115.1739, 36.1147], name: "Las Vegas Strip", category: "landmark", blurb: "Neon and showmanship" },
  { coord: [-104.8716, 38.8776], name: "Garden of the Gods", category: "landmark", blurb: "Red sandstone fins" },
  { coord: [-90.1850, 38.6247], name: "Gateway Arch (St. Louis)", category: "landmark", blurb: "630 ft stainless steel" },
  { coord: [-87.6234, 41.8826], name: "Cloud Gate (The Bean)", category: "landmark", blurb: "Mirrored sculpture, Chicago" },
  { coord: [-77.0353, 38.8895], name: "Lincoln Memorial", category: "landmark", blurb: "Marble seat of Honest Abe" },
  { coord: [-74.0445, 40.6892], name: "Statue of Liberty", category: "landmark", blurb: "Lady Liberty herself" },
  { coord: [-71.0566, 42.3601], name: "Faneuil Hall (Boston)", category: "landmark", blurb: "Cradle of Liberty" },
  { coord: [-77.0500, 38.8893], name: "Washington Monument", category: "landmark", blurb: "555 ft obelisk" },
  { coord: [-81.5639, 41.4995], name: "Rock & Roll Hall of Fame", category: "landmark", blurb: "Cleveland's music shrine" },
  { coord: [-86.5740, 34.7128], name: "U.S. Space & Rocket Center", category: "landmark", blurb: "Saturn V on display" },
  { coord: [-80.6500, 28.5728], name: "Kennedy Space Center", category: "landmark", blurb: "NASA launch site" },

  // ===== Roadside Quirks — World's Largest, etc. =====
  { coord: [-89.4884, 39.8003], name: "World's Largest Catsup Bottle (Collinsville, IL)", category: "quirky", blurb: "170 ft of tomato glory" },
  { coord: [-93.2245, 45.5808], name: "World's Largest Ball of Twine (Darwin, MN)", category: "quirky", blurb: "17,400 lbs of sisal" },
  { coord: [-99.5040, 38.8410], name: "World's Largest Ball of Twine (Cawker City, KS)", category: "quirky", blurb: "Still growing" },
  { coord: [-90.6307, 32.4587], name: "Mississippi Petrified Forest", category: "quirky", blurb: "36-million-year-old logs" },
  { coord: [-101.9698, 35.1872], name: "Cadillac Ranch (Amarillo, TX)", category: "quirky", blurb: "10 buried Cadillacs you can spray-paint" },
  { coord: [-96.6730, 33.0455], name: "Eiffel Tower (Paris, TX)", category: "quirky", blurb: "65 ft replica with cowboy hat" },
  { coord: [-104.9420, 41.1399], name: "World's Largest Steam Locomotive (Cheyenne, WY)", category: "quirky", blurb: "Big Boy 4004" },
  { coord: [-115.4513, 36.6840], name: "Seven Magic Mountains", category: "quirky", blurb: "Towering neon boulders in the desert" },
  { coord: [-112.1860, 33.4570], name: "World's Largest Sundial (Carefree, AZ)", category: "quirky", blurb: "90 ft across" },
  { coord: [-90.1995, 38.6259], name: "City Museum (St. Louis)", category: "quirky", blurb: "Repurposed industrial playground" },
  { coord: [-118.4126, 33.9858], name: "Randy's Donuts (LA)", category: "quirky", blurb: "Giant donut on the roof" },
  { coord: [-86.7816, 36.1622], name: "Parthenon Replica (Nashville)", category: "quirky", blurb: "Full-scale Greek temple" },
  { coord: [-90.1986, 34.7569], name: "Graceland (Memphis)", category: "landmark", blurb: "Elvis's mansion" },
  { coord: [-88.9445, 30.4060], name: "USS Alabama Battleship", category: "landmark", blurb: "WWII battleship museum" },
  { coord: [-104.4760, 33.3943], name: "International UFO Museum (Roswell)", category: "quirky", blurb: "Aliens, allegedly" },
  { coord: [-116.5260, 35.2872], name: "Calico Ghost Town", category: "quirky", blurb: "Restored Old West silver town" },
  { coord: [-95.3692, 41.2589], name: "Carhenge (Alliance, NE)", category: "quirky", blurb: "Stonehenge made of cars" },
  { coord: [-100.3290, 43.9695], name: "Wall Drug (SD)", category: "quirky", blurb: "Free ice water since 1931" },
  { coord: [-118.3370, 33.7621], name: "Queen Mary (Long Beach)", category: "landmark", blurb: "Retired ocean liner" },

  // ===== Canada / Mexico classics =====
  { coord: [-79.0742, 43.0828], name: "Niagara Falls", category: "landmark", blurb: "Horseshoe Falls thunder" },
  { coord: [-115.5708, 51.4254], name: "Banff National Park", category: "national-park", blurb: "Turquoise lakes & Rockies" },
  { coord: [-116.2188, 52.8810], name: "Jasper National Park", category: "national-park", blurb: "Icefields Parkway end" },
];

/** Distance (meters) from a point to the nearest vertex of a polyline. */
function nearestRouteDist(coord: LngLat, route: LngLat[]): number {
  let best = Infinity;
  for (const v of route) {
    const dx = (v[0] - coord[0]) * 90000;
    const dy = (v[1] - coord[1]) * 111000;
    const d = Math.hypot(dx, dy);
    if (d < best) best = d;
    if (best < 100) return best;
  }
  return best;
}

/** Find epic stops within `maxDetourM` of the route polyline. */
export function findEpicStopsAlongRoute(
  route: LngLat[],
  maxDetourM: number,
): EpicStop[] {
  if (route.length < 2) return [];
  const out: EpicStop[] = [];
  for (const stop of EPIC_ROAD_TRIP_STOPS) {
    if (nearestRouteDist(stop.coord, route) <= maxDetourM) {
      out.push(stop);
    }
  }
  return out;
}
